import type { ProductProperties, ProductVariant } from '~/types/product';

type PropertyDef = Pick<ProductProperties, 'key' | 'dependsOn' | 'values'>;

/**
 * Collect all transitive descendant keys of `parentKey` in a property dependency chain.
 * Pure function — only inspects the `dependsOn` graph, no side-effects.
 */
export function getDescendantKeys(
  properties: Pick<ProductProperties, 'key' | 'dependsOn'>[],
  parentKey: string
): string[] {
  const directChildren = properties.filter((p) => p.dependsOn === parentKey);
  return directChildren.flatMap((child) => [
    child.key,
    ...getDescendantKeys(properties, child.key),
  ]);
}

/**
 * Normalise a string for comparison: lowercase, trim, replace underscores with spaces.
 * Converts both "heavy_duty" (Record key) and "heavy duty" (stored value) to "heavy duty".
 */
export function normalizePropertyValue(s: string): string {
  return s.toLowerCase().trim().replace(/_/g, ' ');
}

type SelectedProperty = { key: string; value?: string | null };

const NON_DIMENSION_ATTRIBUTE_KEYS = new Set(['hcpc', 'icds']);

function ownDimensionKeys(variant: ProductVariant): string[] {
  return Object.keys(variant.attributes).filter((key) => !NON_DIMENSION_ATTRIBUTE_KEYS.has(key));
}

function variantDimensionKeys(variants: ProductVariant[]): Set<string> {
  return new Set(variants.flatMap(ownDimensionKeys));
}

function selectedValueMap(selected: SelectedProperty[]): Map<string, string> {
  return new Map(
    selected
      .filter((prop) => prop.key !== 'additional_information' && prop.value)
      .map((prop) => [prop.key, prop.value ?? ''])
  );
}

function dimensionMatches(variant: ProductVariant, key: string, selectedValue: string): boolean {
  const attr = variant.attributes[key];
  return (
    attr != null && normalizePropertyValue(String(attr)) === normalizePropertyValue(selectedValue)
  );
}

/**
 * A variant matches when every dimension on that variant is selected and agrees.
 * Extra saved keys and sibling-only fields are ignored.
 */
export function findMatchingVariant(
  variants: ProductVariant[],
  selected: SelectedProperty[]
): ProductVariant | undefined {
  const selectedByKey = selectedValueMap(selected);
  const named = variants.filter((variant) => ownDimensionKeys(variant).length > 0);
  const pool = named.length > 0 ? named : variants;

  const candidates = pool.filter((variant) =>
    ownDimensionKeys(variant).every((key) => {
      const value = selectedByKey.get(key);
      return value != null && value !== '' && dimensionMatches(variant, key, value);
    })
  );

  if (candidates.length === 0) return undefined;

  return candidates.reduce((best, variant) =>
    ownDimensionKeys(variant).length > ownDimensionKeys(best).length ? variant : best
  );
}

export function unselectedVariantDimensionLabels(
  variants: ProductVariant[],
  selected: SelectedProperty[],
  propertyDefs: Array<{ key: string; displayText?: string }> = []
): string[] {
  const selectedByKey = new Map(selected.map((prop) => [prop.key, prop.value]));
  const compatible = variants.filter((variant) => {
    const keys = ownDimensionKeys(variant);
    if (keys.length === 0) return false;
    return keys.every((key) => {
      const value = selectedByKey.get(key);
      if (value == null || value === '') return true;
      return dimensionMatches(variant, key, value);
    });
  });

  const firstCompatible = compatible[0];
  if (firstCompatible == null) return [];

  const sharedKeys = ownDimensionKeys(firstCompatible).filter((key) =>
    compatible.every((variant) => ownDimensionKeys(variant).includes(key))
  );

  return sharedKeys.flatMap((key) => {
    const value = selectedByKey.get(key);
    if (value != null && value !== '') return [];
    const label =
      propertyDefs.find((prop) => prop.key === key)?.displayText ?? key.replaceAll('_', ' ');
    return [label];
  });
}

export function isPropertyApplicable(
  property: PropertyDef,
  selected: Map<string, string>,
  variants: ProductVariant[]
): boolean {
  if (property.dependsOn == null || property.dependsOn === '') return true;
  return filterPropertyOptions(property, selected, variants).length > 0;
}

/**
 * Resolve the base option list from the property's `values` field.
 * Handles both flat arrays and `dependsOn` Record lookups with key normalisation.
 */
function getBaseValues(property: PropertyDef, selected: Map<string, string>): string[] {
  // Independent property — values is a flat array
  if (Array.isArray(property.values)) return property.values;

  // Dependent property — look up parent selection in the Record
  if (property.dependsOn == null) return [];

  const parentValue = selected.get(property.dependsOn);
  if (parentValue == null || parentValue === '') return [];

  const normalizedParent = normalizePropertyValue(parentValue);
  const record = property.values as Record<string, string[]>;

  for (const key of Object.keys(record)) {
    if (normalizePropertyValue(key) === normalizedParent) {
      return record[key] ?? [];
    }
  }

  return [];
}

/**
 * Compute the visible options for a property selector.
 *
 * 1. Resolves base options from `dependsOn` (with key normalisation — fixes
 *    the underscore/space mismatch bug).
 * 2. If `variants` are available, further restricts options to those that
 *    appear in variants matching all other selected properties (fixes the
 *    multi-level cascade bug).
 * 3. Falls back to base options when variant data provides no constraint
 *    (backwards-compatible for products without variant coverage).
 */
export function filterPropertyOptions(
  property: PropertyDef,
  selected: Map<string, string>,
  variants: ProductVariant[]
): string[] {
  const base = getBaseValues(property, selected);

  // No variants → return base options unchanged (backwards compat)
  if (variants.length === 0) return base;

  // Only constrain on keys that actually appear in variant attributes.
  // Non-variant properties (e.g. text fields from savedProperties) would
  // fail the attribute lookup for every variant, emptying the filter and
  // silently falling back to base options.
  const variantKeys = variantDimensionKeys(variants);
  const constraints: Array<[string, string]> = [];
  for (const [key, value] of selected) {
    if (key !== property.key && value !== '' && variantKeys.has(key)) {
      constraints.push([key, normalizePropertyValue(value)]);
    }
  }

  // Filter variants to those matching all constraints. Skip a constraint when
  // this variant has no such dimension (Adult has no Size; leftover Size must
  // not hide Adult from the Option list).
  const filtered = variants.filter((variant) =>
    constraints.every(([key, normValue]) => {
      const attr = variant.attributes[key];
      if (attr == null) return true;
      return normalizePropertyValue(String(attr)) === normValue;
    })
  );

  // Collect the valid values for this property from filtered variants
  const validSet = new Set<string>();
  for (const variant of filtered) {
    const attr = variant.attributes[property.key];
    if (attr != null) {
      validSet.add(normalizePropertyValue(String(attr)));
    }
  }

  // No variant coverage for this property → return base unchanged (don't over-restrict)
  if (validSet.size === 0) return base;

  // Intersect with base to preserve display order
  return base.filter((v) => validSet.has(normalizePropertyValue(v)));
}
